/* The Outreach panel: her sequences, and the three touches each one is made of.
 *
 * REDESIGNED 2026-09-16 from her spec (docs/outreach-sequences-redesign-spec.md):
 *   • Add asks WHAT THE SEQUENCE IS FOR first — Meeting Follow-up, Outbound
 *     Sales Outreach, Inbound Sales, Customer Success — not where it goes out.
 *   • The CHANNEL belongs to each touch: Email, Text / SMS, LinkedIn,
 *     Instagram, Facebook. One sequence can open on email and close by text.
 *     Limits and the subject line follow the touch's own channel.
 *   • The table gains Type and Channels (a badge per channel used).
 *
 * WHERE IT FOLLOWS CURRENT BEHAVIOUR RATHER THAN THE SPEC'S WORDING, her rule
 * 2026-09-16 ("use current behavior … website is the model"):
 *   • Touches are the shared Block row (block.jsx), stacked like the sections
 *     on a Sites page. The spec asked for "the one-at-a-time stepper used in
 *     Sites' section editor"; on Sites that stepper lives in the drafting chat,
 *     and the page itself is a stack of these rows. The stepper comes to
 *     Outreach with its drafting chat (item 4).
 *   • Done is her mark on the SEQUENCE (the page/module rule since
 *     2026-09-15), not "Locked" on each touch as the mockup draws it.
 *   • The Type pill is neutral, like a site's kind (her call 2026-09-15:
 *     colour is spent on state, not category).
 *   • A touch's chat is the shared drafting screen (drafter.jsx), the same one
 *     Sites and Courses use — including its one-at-a-time pager, which is where
 *     the spec's "stepper" lives. No sparkle yet: the sparkle drafts in place
 *     from a brief, and a touch has none to draft from until a sequence has a
 *     goal worth writing to. The chat asks.
 */
import { useState } from "react";
import {
  CHANNELS, MIDDLE_ROLES, SEQUENCE_STAGES, SEQUENCE_TYPES, TOUCH_STATE_LABEL, stageBands, channelDef, isMiddleTouch, openingLine, overLimit, roleDef, touchLabel,
  applyTouchBlock, buildSequenceContext, buildTouchContext, parseSequenceBlock, parseTouchBlock, sequenceChannels, sequenceLabel, sequenceState,
  sequenceTypeDef, sequenceWords, stripTouchBlock, touchChars, touchKindOf, touchState, touchWords,
} from "../../utils/outreach";
import { BrowseHead, BrowseRow, SortableItem, SortableList, browse, useBrowseNarrow } from "../BrowseTable";
import { Block } from "./block";
import { Drafter } from "./drafter";
import { useRecordKeep } from "../../utils/pageChat";
import { buildMomentumContext } from "../../utils/momentumContext";
import { SectionStatus } from "./fields";
import { rungLabel, rungTag } from "../../utils/offerRung";
import { B, STATUS_DOT, blockIcon, btnGhost, btnPrimary, input, nb, pDesc, pTitle } from "./tokens";
import { AddButton, Badge, Bread, ChatWithBrill, Menu2, Note, PageTitleField, SavedTick, Status } from "./ui";
/* The bar, the rail and the footer are the same on every panel — shell.jsx. */
import { BarBack, MomentumBar, RecordCopy, RecordFooter } from "./shell";
import { EmailCopy } from "./emailCopy";
import { useSession } from "@clerk/clerk-react";
import { useCrmConnected } from "./mediaPicker";
import { copyText, readEmailFormat } from "../../utils/emailHtml";
import { sendEmailTemplate, touchParts, touchTemplateName, workflowSheet } from "../../utils/ghlHandoff";
import { Settings, Trash2 } from "lucide-react";

/* What a sequence is for, as a pill. A sequence made before types existed says
   so in amber — it is something still to set, which is what amber means. */
export function TypePill({ type }) {
  const def = sequenceTypeDef(type);
  if (!def) {
    return <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, color: B.amber, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, whiteSpace: "nowrap" }}>Set type</span>;
  }
  return <span style={{ whiteSpace: "nowrap" }}><Badge cls="neutral" text={def.label} /></span>;
}

/* One small badge per channel a sequence uses — the table's Channels column. */
export function ChannelBadges({ touches = [] }) {
  const list = sequenceChannels(touches);
  if (!list.length) return <span style={{ fontSize: 12, color: B.ghost }}>—</span>;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {list.map((c) => (
        <span key={c.id} title={c.label} style={{ padding: "2px 6px", borderRadius: 5, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: B.teal, background: B.tealQuiet, border: `1px solid ${B.tealLine}` }}>{c.short}</span>
      ))}
    </span>
  );
}

/* The `Status` component's three classes, from one state word — so the table,
   the rail and the header can't drift. */
const STATE_CLS = { done: "locked", draft: "draft", empty: "retired" };
export function StateTag({ state }) {
  return <Status cls={STATE_CLS[state] || "draft"} text={TOUCH_STATE_LABEL[state] || "Draft"} />;
}


/* ── The shell ────────────────────────────────────────────────────────────────
   Both views sit in it, so the panel's own words are written once. */
export function OutreachShell({ sequences = [], view, setView, error, onDismissError, menuOpen, setMenuOpen, onAdd, draftingBack = null, children }) {
  /* While she is drafting, at ANY width (2026-09-24 — until then a phone
     only): the panel's chrome gives the screen up to the chat, and
     MomentumBar stands down too. The drafting screen's one row is all that
     sits above the conversation. */
  const bare = !!draftingBack;
  return (
    <>
      {/* THE PANEL'S OWN HEAD STANDS DOWN ON A PHONE WHILE SHE DRAFTS. Her
          report, 2026-09-22: the crumb, the heading and the description were
          three of the rows that pushed the conversation down to four lines and
          the input below the fold. None of them answers a question she has
          while writing a block — the drafting screen's one row names where she
          is, and the chat card names what it is writing. */}
      {!bare && (<>
      <Bread text="Grow" />
      <h2 style={pTitle}>Outreach Sequences</h2>
      {/* Her words from the spec, as drafted — less "this is Funnels' mirror
          image": Funnels were retired on 2026-08-22 (c53c732). */}
      <p style={pDesc}>
        A few short touches, sorted by stage: Trust Building earns the yes, Core reaches clients in your
        main program, Continuation keeps them going after it. Pick what this sequence is for, then
        choose LinkedIn, Instagram, Facebook, Email or Text for each touch on its own — a meeting
        reminder might open on email and close with a text. Written by you, never sent automatically.
      </p>
      </>)}

      {/* WHAT IT IS FOR is chosen on the way IN (her spec, point 1). Channel is
          not asked here any more — it belongs to each touch.

          No Level 1 slot on this bar, on purpose — spec 8.5. */}
      <MomentumBar view={view} setView={setView} copyLabel="Sequence copy" gap={20} levels={2} drafting={!!draftingBack}
        /* THE WAY BACK OUT OF THE DRAFTING SCREEN, before Add — scope order,
           and the row Sites reads. The drafting screen used to draw its own
           back UNDER this bar, which put "Add sequence" above the way out of
           the thing she was writing. It draws none now (chromeInBar), so an
           empty `draftingBack` is a screen with no way back. */
        /* NOT ON THE TABLE. The drafting state lives above this and does not
           clear when she switches view, so without this she could be looking at
           the list of sequences while the bar offered her the way back out of one she
           is not on screen with. The drafting screen's own back could never do
           that — it was inside the screen. */
        lead={view !== "table" && draftingBack && <BarBack {...draftingBack} />}
        add={
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add sequence" onClick={() => setMenuOpen(!menuOpen)} />
          {menuOpen && (
            <Menu2 head="What's this sequence for?" items={SEQUENCE_STAGES.flatMap((stage) => [
              rungLabel(stage),
              ...SEQUENCE_TYPES.filter((t) => t.stage === stage).map((t) => [
                "✉️", t.label, t.hint, false,
                () => { setMenuOpen(false); onAdd(t.id); },
              ]),
            ])} />
          )}
        </div>
      } />

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          <b>Your sequences didn't save.</b> {error} Nothing was lost — try again once you're back online.
        </div>
      )}

      {children}

      {sequences.length > 0 && (
        <Note>
          <b style={nb}>One arc, as many touches as it needs:</b> it always opens warm and ends with a
          close that leaves the door open; what goes between is yours. No cadence bar, no send times —
          this reads like one woman reaching out to another, not a sequence running itself.
        </Note>
      )}
    </>
  );
}


/* ── Table view ───────────────────────────────────────────────────────────────
   Every sequence at a glance, and where reordering and deleting live. */
export const OUTREACH_COLS = "1.8fr 1.1fr .8fr 1fr .55fr 1fr .55fr .8fr 40px";

export function OutreachTable({ sequences = [], touchesFor, offers = [], loading, onOpen, onDelete, onReorder }) {
  /* Below 640px these rows stop being a grid — see BrowseRow. */
  const narrow = useBrowseNarrow();

  // Only the FIRST load blanks the table: an empty table reads as "you have no
  // sequences" rather than "still loading". Same rule as the Offer Ladder.
  if (loading && sequences.length === 0) {
    return (
      <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 30, textAlign: "center", color: B.dim, fontSize: 13 }}>
        Loading your sequences…
      </div>
    );
  }
  if (sequences.length === 0) {
    return (
      <div style={{ border: `1px dashed ${B.border2}`, borderRadius: 10, padding: 30, textAlign: "center", color: B.dim, fontSize: 13, lineHeight: 1.6 }}>
        No sequences yet. Click <b style={nb}>Add sequence</b>, pick what it's for, and your first
        three touches are waiting for you — add as many more as the sequence needs.
      </div>
    );
  }

  return (
    <div style={browse}>
      <BrowseHead cols={OUTREACH_COLS} narrow={narrow}
        labels={["Sequence", "Type", "Channels", "Goal", "Written", "Connected offer", "Words", "Status", ""]} />
      {/* BANDED BY STAGE, like the Offer Ladder (her call, 2026-10-01) — so
          the three screens read the same: Trust Building · Core · Continuation.
          A sequence's stage is its linked offer's, else its type's
          (sequenceStage). The grip reorders WITHIN a band; the move is handed
          up as positions in the whole list, which keeps the band's order. */}
      {stageBands(sequences, offers).map((band) => (
      <div key={band.key}>
        <div style={{ padding: "9px 14px", background: B.surface2, borderTop: `1px solid ${B.border}`, borderBottom: `1px solid ${B.border}`,
          fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label }}>
          {band.label}
        </div>
      <SortableList ids={band.rows.map((s) => s.id)}
        onReorder={onReorder ? (from, to) => onReorder(sequences.indexOf(band.rows[from]), sequences.indexOf(band.rows[to])) : undefined}>
        {band.rows.map((s) => {
          const touches = touchesFor(s.id);
          const words = sequenceWords(touches);
          const state = sequenceState(s, touches);
          const written = touches.filter((t) => touchState(t) !== "empty").length;
          const opener = openingLine(touches);
          const offer = offers.find((o) => o.id === s.offerId);
          const rung = rungTag(s, offers);
          return (
            <SortableItem key={s.id} id={s.id}>
              {({ handle, dragging }) => (
                <BrowseRow cols={OUTREACH_COLS} narrow={narrow} onClick={() => onOpen(s)}
                  style={{ cursor: "pointer", opacity: dragging ? 0.4 : 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    {/* stopPropagation, or reaching for the grip opens the sequence. */}
                    <span style={{ display: "flex", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>{handle}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: s.name ? B.text : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {sequenceLabel(s)}
                      </div>
                      {/* The opening line — the words a stranger reads first. */}
                      <div style={{ fontSize: 11, color: opener ? B.faint : B.ghost, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {opener || "No copy yet"}
                      </div>
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}><TypePill type={s.type} /></div>
                  <div style={{ minWidth: 0 }}><ChannelBadges touches={touches} /></div>
                  <div style={{ minWidth: 0, fontSize: 12.5, color: s.goal ? B.mid : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.goal || "—"}
                  </div>
                  {/* How many of the three have words in them. */}
                  <div style={{ fontSize: 12.5, color: written ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>{written} / {touches.length || 3}</div>
                  <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ minWidth: 0, fontSize: 12.5, color: offer ? B.mid : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {offer ? offer.name || "Untitled offer" : "—"}
                    </span>
                    {/* WHICH RUNG, derived from that offer and never stored here
                        (utils/offerRung) — in the one set of stage names
                        (her call, 2026-10-01). */}
                    {rung ? <span style={{ whiteSpace: "nowrap" }}><Badge cls={rung.badge} text={rung.label} /></span> : null}
                  </div>
                  <div style={{ fontSize: 12.5, color: words ? B.mid : B.ghost, fontVariantNumeric: "tabular-nums" }}>{words || "—"}</div>
                  <div><StateTag state={state} /></div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => onDelete(s)} title="Delete sequence" aria-label="Delete sequence" style={blockIcon}><Trash2 size={14} /></button>
                  </div>
                </BrowseRow>
              )}
            </SortableItem>
          );
        })}
      </SortableList>
      </div>
      ))}
    </div>
  );
}




/* ── Sequence copy ────────────────────────────────────────────────────────────
   The sequences down the side, the one she's writing beside them. The rail is
   `RecordCopy` (shell.jsx); a row reads the name, what the sequence is for, and
   the same dot the table's Status column shows — pressable, like a page's or a
   module's, to mark it Done. */
export function OutreachCopy({ sequences = [], touchesFor, selectedId, onSelect, onReorder, onMark, drafting, onExitToTable, children }) {
  return (
    <RecordCopy
      /* Writing one touch is its own two-column screen, so the rail steps out
         of the way — the same call Sites and Courses make. */
      bare={drafting}
      /* TWO LEVELS: the Table is this list, so a phone skips the list screen
         and comes straight back here. See `levels` in shell.jsx. */
      levels={2} onExit={onExitToTable} exitLabel="All sequences"
      records={sequences} selectedId={selectedId} onSelect={onSelect} onReorder={onReorder} onMark={onMark}
      noun="sequence"
      rowFor={(s) => {
        const state = sequenceState(s, touchesFor(s.id));
        return {
          title: sequenceLabel(s),
          meta: sequenceTypeDef(s.type)?.label || "No type yet",
          dot: B[STATUS_DOT[state]],
          dotTitle: TOUCH_STATE_LABEL[state],
          markable: state !== "empty",
          done: state === "done",
        };
      }}>
      {children}
    </RecordCopy>
  );
}


/* ── One sequence ─────────────────────────────────────────────────────────────
   Its name is the heading. Type, goal and connected offer live behind the cog
   (rule 8) and are SHOWN in the line under the name. Draft / Done sits at the
   top, where the state is read — the same place a module carries it. */
export function OutreachSequence({
  sequence, touches = [], offers = [], error,
  onSave, onDelete, onSaveTouch, onClearTouch, onBackToTable, onWrite, onDraftTouch, onSparkleTouch, sparklingTouchId = null, draftedTouch = null,
  onAddTouch, onRemoveTouch, onReorderTouches, brand = {},
}) {
  /* Typed into local state and written on the way out; this editor is keyed by
     sequence id at the call site, so the drafts can't leak between sequences. */
  const [name, setName] = useState(sequence?.name || "");
  /* A sequence with no type opens with its settings UP — setting it is the one
     thing the redesign asks of every sequence made before it. After that they
     live behind the cog (her call, 2026-09-16: "a little much for it to
     remain" — pop up, Save, then into settings). */
  /* OPEN ON A SEQUENCE THAT IS STILL BLANK. Rule 8 said the settings pop up by
     themselves only while a sequence has no type — but Add asks for the type in
     its menu, so a new sequence always had one and the settings never opened,
     leaving her on a screen with a nameless sequence and no obvious next move
     (her report, 2026-09-17: "when you add a sequence it doesn't pop open the
     settings"). Blank now means no type, OR no name yet: the two states where
     the settings are the next thing to do. Once it has a name they stay shut. */
  const [cog, setCog] = useState(() => !sequenceTypeDef(sequence?.type) || !String(sequence?.name || "").trim());

  if (!sequence) return null;

  const state = sequenceState(sequence, touches);
  const words = sequenceWords(touches);
  const offer = offers.find((o) => o.id === sequence.offerId);
  const rung = rungTag(sequence, offers);
  const set = (k, v) => onSave({ ...sequence, [k]: v });

  return (
    <>
      {/* What this sequence IS, before what it says. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
        <button onClick={() => setCog(true)} title="Change what this sequence is for"
          style={{ padding: 0, border: "none", background: "none", cursor: "pointer" }}>
          <TypePill type={sequence.type} />
        </button>
        {/* Who she is writing to, derived from the connected offer's rung. Not
            asked for here and not stored here: move the offer and this follows. */}
        {rung ? <Badge cls={rung.badge} text={rung.label} /> : null}
        {/* DONE IS HER MARK, at the top where the state is read — the same
            control, in the same place, as a module. An empty sequence keeps a
            read-out: marking nothing Done is a claim about nothing. */}
        {state === "empty" ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: B.mid }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: B[STATUS_DOT[state]] }} />
            {TOUCH_STATE_LABEL[state]}
          </span>
        ) : (
          <SectionStatus value={sequence.status === "done" ? "done" : "draft"} onChange={(status) => set("status", status)} />
        )}
        <span style={{ fontSize: 11.5, color: B.faint }}>{words ? `${words} words` : "No copy yet"}</span>
        <SavedTick problem={error ? "That didn't save — try again" : ""} />
        {/* The way into writing it with Brill, the same button a page has:
            it opens the drafting screen on the first touch with no words. */}
        {onWrite && (
          <span style={{ marginLeft: "auto", display: "flex" }}>
            <ChatWithBrill label="Write this sequence with Brill" onClick={onWrite} />
          </span>
        )}
        <button onClick={() => setCog(!cog)} title="Sequence settings" aria-label="Sequence settings"
          aria-expanded={cog}
          style={{ ...btnGhost, marginLeft: onWrite ? 0 : "auto", padding: "7px 12px", fontSize: 12, display: "flex", alignItems: "center", gap: 6, color: cog ? B.teal : B.mid, borderColor: cog ? B.tealLine : B.border2 }}>
          <Settings size={13} /> Settings
        </button>
      </div>

      <div style={{ display: "flex", marginBottom: 14 }}>
        <PageTitleField value={name} onChange={setName} placeholder="Name this sequence" label="Sequence name"
          onCommit={() => { if (name !== (sequence.name || "")) set("name", name); }} />
      </div>

      {cog && (
        <SequenceSettings sequence={sequence} offers={offers}
          onSave={(patch) => { onSave({ ...sequence, ...patch }); setCog(false); }}
          onClose={() => setCog(false)} />
      )}
      {/* What the settings say, read-only, so she never has to open them to
          know. Pressing it opens them. */}
      <button onClick={() => setCog(true)} title="Change these in settings"
        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, marginBottom: 18, padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
        <span style={{ color: sequence.goal ? B.mid : B.ghost }}>{sequence.goal || "No goal set"}</span>
        <span style={{ color: B.faint }}>·</span>
        <span style={{ color: offer ? B.mid : B.ghost }}>{offer ? `Connected to ${offer.name || "an untitled offer"}` : "No offer connected"}</span>
      </button>

      {/* AS MANY TOUCHES AS IT NEEDS (her call, 2026-09-16). The opening and
          the close are the two ends and stay put; everything between moves by
          its grip and can be removed. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SortableList ids={touches.map((t) => t.id)} onReorder={(from, to) => onReorderTouches?.(sequence.id, from, to)}>
          {touches.map((t) => (
            <SortableItem key={t.id} id={t.id}>
              {({ handle, dragging }) => (
                <TouchBlock
                  /* KEYED BY THE DRAFT STAMP. A Block holds its own copy of
                     `content` from the moment it mounts, so a sparkle wrote the
                     touch, moved the word count in the rail, and left the row
                     itself reading "Not written yet" until something remounted
                     it. Same mechanism Sites and Course use. */
                  key={`${t.id}:${draftedTouch?.touchId === t.id ? draftedTouch.n : 0}`}
                  touch={t} offers={offers} onSave={onSaveTouch} brand={brand} sequenceName={sequence?.name || ""}
                  startOpen={draftedTouch?.touchId === t.id}
                  onClear={isMiddleTouch(t) ? onRemoveTouch : onClearTouch}
                  onChat={onDraftTouch ? () => onDraftTouch(t) : null}
                  sparkle={onSparkleTouch ? {
                    busy: sparklingTouchId === t.id,
                    onClick: () => onSparkleTouch(t),
                    label: touchWords(t) ? "Rewrite with Brill" : "Write with Brill",
                  } : null}
                  dragHandle={isMiddleTouch(t) ? handle : <span style={{ width: 16, flexShrink: 0 }} />}
                  dragging={dragging} />
              )}
            </SortableItem>
          ))}
        </SortableList>
      </div>
      {onAddTouch && (
        <div style={{ marginTop: 12 }}>
          <button onClick={() => onAddTouch(sequence)} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>+ Add touch</button>
        </div>
      )}

      <SequenceToGhl sequence={sequence} touches={touches} brand={brand} />

      {/* Delete, at the bottom of the thing it deletes. */}
      <RecordFooter tick={false} onDelete={() => onDelete(sequence)} />
    </>
  );
}


/* ── The sequence, taken to GHL (her ask, 2026-10-12) ───────────────────────
   GHL's API cannot build a workflow. So: every EMAIL touch goes over as a
   template in one press (admin, like every GHL button), and the WORKFLOW
   SHEET says step by step what to build — which template, which message,
   where the waits go. The sheet is copy, so it works for everyone. */
export function SequenceToGhl({ sequence, touches = [], brand = {} }) {
  const crm = useCrmConnected();
  const { session } = useSession();
  const [results, setResults] = useState(null);   // [{ label, ok, text }]
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState(null);
  const emails = touches.filter((t) => channelDef(t.channel).id === "email" && String(t.body || "").trim());
  if (!touches.length) return null;
  const sheet = workflowSheet(sequence, touches);

  /* One at a time, in her order — GHL is asked for each template by name, and
     two at once could both miss and both create. */
  const sendAll = async () => {
    if (busy || !emails.length) return;
    setBusy(true);
    setResults(null);
    const token = await session?.getToken().catch(() => null);
    const format = readEmailFormat("outreach", "plain");
    const out = [];
    for (const t of [...emails].sort((a, b) => (a.position || 0) - (b.position || 0))) {
      const r = await sendEmailTemplate({ token, name: touchTemplateName(sequence?.name, t), format, parts: touchParts(t), brand });
      out.push({ label: `Touch ${t.position}`, ok: r.ok, text: r.ok ? `${r.updated ? "updated" : "saved"} as “${r.name}”` : r.error });
      setResults([...out]);
    }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${B.border}` }}>
      <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 10, lineHeight: 1.6 }}>
        Take this sequence to GHL. GHL can't build a workflow for you{crm ? ", so Brill sends the emails as templates and" : " — Brill"} writes the steps to build it.
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {crm && (
          <button onClick={sendAll} disabled={busy || !emails.length}
            title={emails.length ? undefined : "No written email touches yet"}
            style={{ ...btnPrimary, padding: "9px 15px", fontSize: 12.5, opacity: busy || !emails.length ? 0.6 : 1 }}>
            {busy ? "Sending…" : `Send ${emails.length === 1 ? "the email" : `all ${emails.length} emails`} to GHL`}
          </button>
        )}
        <button onClick={() => setOpen((v) => !v)} aria-expanded={open} style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
          {open ? "Hide the workflow steps" : "Workflow steps"}
        </button>
        <button onClick={async () => { const r = await copyText(sheet); setSaid(r.ok ? "Copied — the steps to build it in GHL." : `It didn't copy: ${r.error}`); }}
          style={{ ...btnGhost, padding: "9px 15px", fontSize: 12.5 }}>
          Copy the steps
        </button>
      </div>
      {results && (
        <ul aria-label="Sent to GHL" style={{ margin: "10px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
          {results.map((x) => <li key={x.label} style={{ color: x.ok ? B.text : B.amber }}>{x.label}: {x.text}</li>)}
        </ul>
      )}
      {said && <div role="status" style={{ marginTop: 8, fontSize: 12, color: B.dim }}>{said}</div>}
      {open && (
        <pre style={{ margin: "12px 0 0", padding: "12px 14px", borderRadius: 10, border: `1px solid ${B.border}`, background: B.surface2, color: B.text, fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
          {sheet}
        </pre>
      )}
    </div>
  );
}


/* ── A sequence's settings, as a pop-up ──────────────────────────────────────
   What it's for, its goal and its offer. A pop-up rather than a drawer (her
   call, 2026-09-16), so it holds a DRAFT and writes it on Save — the one place
   Outreach has a Save button, because a dialog that saved as she typed would
   leave Cancel meaning nothing. Save waits for a type: that is the question
   the pop-up exists to ask. Escape and the backdrop cancel. */
export function SequenceSettings({ sequence, offers = [], onSave, onClose }) {
  const [draft, setDraft] = useState({ type: sequence?.type || null, goal: sequence?.goal || "", offerId: sequence?.offerId || null });
  const put = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const def = sequenceTypeDef(draft.type);
  return (
    <div onClick={onClose} onKeyDown={(e) => { if (e.key === "Escape") onClose?.(); }}
      style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label="Sequence settings" onClick={(e) => e.stopPropagation()}
        style={{ background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14, padding: "20px 20px 16px", width: "100%", maxWidth: 520, maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", boxSizing: "border-box" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 16 }}>{sequence?.name || "This sequence"}</div>
        <Setting label="What it's for" hint={def ? def.hint : "Pick what this sequence is for."}>
          <select autoFocus value={draft.type || ""} onChange={(e) => put("type", e.target.value || null)} aria-label="Sequence type" style={input}>
            {!draft.type && <option value="">Choose one…</option>}
            {SEQUENCE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Setting>
        <Setting label="Goal" hint="What this particular sequence is after, in your words — reduce no-shows, a warm connection, an invite to your free offer.">
          <input value={draft.goal} onChange={(e) => put("goal", e.target.value)}
            placeholder="e.g. Reduce no-shows" aria-label="Goal" style={input} />
        </Setting>
        <Setting label="Connected offer" hint="Which rung of your ladder this sequence is walking someone toward. The value-give is where it gets mentioned.">
          <OfferSelect offers={offers} value={draft.offerId} onChange={(v) => put("offerId", v)} label="Connected offer" />
        </Setting>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
          <button onClick={onClose} style={{ ...btnGhost, padding: "9px 16px", fontSize: 13 }}>Cancel</button>
          <button onClick={() => onSave?.({ type: draft.type, goal: draft.goal, offerId: draft.offerId })} disabled={!def}
            title={def ? "Save" : "Pick what it's for first"}
            style={{ ...btnPrimary, padding: "9px 18px", fontSize: 13, opacity: def ? 1 : 0.5, cursor: def ? "pointer" : "not-allowed" }}>Save</button>
        </div>
      </div>
    </div>
  );
}


/** One row of the settings form. */
function Setting({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}


/** The offer picker, used at both levels. `offerId` is a real foreign key, so
 *  the empty string maps to null. */
function OfferSelect({ offers = [], value, onChange, label }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value || null)} aria-label={label} style={input}>
      <option value="">No offer</option>
      {offers.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
    </select>
  );
}


/* ── One touch ────────────────────────────────────────────────────────────────
   The shared Block (block.jsx) — the website section's row. What only a touch
   has rides in its slots:
     · its CHANNEL, as a pill on the header that is also the picker, so which
       channel each touch goes out on reads at a glance down the stack, and
       changes without opening anything;
     · the character count against that channel's limit;
     · the role's coaching copy and the connected free offer, in the editor.
   Clearing asks first (the hub's `askClearTouch`). */
export function TouchBlock({ touch, offers = [], onSave, onClear, onChat, sparkle = null, startOpen = false, dragHandle, dragging, brand = {}, sequenceName = "" }) {
  const role = roleDef(touch?.touchRole);
  const channel = channelDef(touch?.channel);
  const kind = touchKindOf(touch);

  return (
    <Block
      kind={kind} noun="touch"
      content={{ subject: touch?.subject || "", body: touch?.body || "" }}
      words={(c) => touchWords({ ...touch, ...c })}
      onCommit={(c) => onSave({ ...touch, ...c })}
      badges={(c) => {
        const live = { ...touch, ...c };
        const chars = touchChars(live);
        const over = overLimit(live);
        return (
          <>
            <ChannelPicker value={channel.id} onChange={(v) => onSave({ ...touch, ...c, channel: v })} />
            {channel.limit && chars ? (
              <span style={{ flexShrink: 0, fontSize: 11, color: over ? B.salmon : B.faint, fontVariantNumeric: "tabular-nums" }}>{chars} / {channel.limit}</span>
            ) : null}
          </>
        );
      }}
      onChat={onChat || undefined}
      /* The one-press write, same as a website section and a course block: she
         gets words without opening the chat first. Her report, 2026-09-17:
         "outreach sequences has no magic button to write the touch points." */
      sparkle={sparkle || undefined}
      /* Open on what Brill just wrote — see the note in callScripts.jsx. */
      startOpen={startOpen}
      /* On a middle touch the bin REMOVES the touch; on the opening and the
         close, which always stay, it clears their words. The panel asks first. */
      onClear={() => onClear?.(touch)}
      clearLabel={isMiddleTouch(touch) ? `Remove touch ${touch?.position || ""}`.trim() : `Clear ${role.label.toLowerCase()}`}
      dragHandle={dragHandle} dragging={dragging}
      /* AN EMAIL TOUCH GOES OUT PLAIN OR DESIGNED (her call, 2026-10-12),
         starting Plain — one person writing to another. */
      readBottom={channel.id === "email" ? (
        <EmailCopy where="outreach" defaultFormat="plain" brand={brand}
          ghlName={`${sequenceName || "Outreach"} · Touch ${touch?.position || ""}`.trim()}
          parts={{ subject: String(touch?.subject || "").trim(), preview: "", sections: [{ heading: "", text: String(touch?.body || "").trim() }], ask: "", fullVersion: "" }} />
      ) : null}
      editTop={
        /* What this touch is FOR, always — as useful on the third rewrite as on
           the blank page. */
        <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.55, marginBottom: 14 }}>
          {/* What a middle touch DOES is hers to say; the ends are fixed. */}
          {isMiddleTouch(touch) && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>This touch is a</span>
              <select value={touch.touchRole} onChange={(e) => onSave({ ...touch, touchRole: e.target.value })} aria-label="What this touch does"
                style={{ ...input, width: "auto", padding: "6px 9px", fontSize: 12, fontWeight: 700 }}>
                {MIDDLE_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
          )}
          {role.empty}
          {!channel.id && <> <b style={{ ...nb, color: B.amber }}>Choose a channel on the row above</b> — it decides the length and whether there's a subject line.</>}
        </div>
      }
      editBottom={(c) => (
        <>
          {overLimit({ ...touch, ...c }) && (
            <div style={{ fontSize: 11.5, color: B.amber, marginTop: -6, marginBottom: 14, lineHeight: 1.5 }}>
              {channel.over || `Longer than ${channel.label} allows in one message. Nothing here is cut — you'll want to trim it before you send.`}
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>Connected free offer</label>
            <OfferSelect offers={offers} value={touch?.offerId} onChange={(v) => onSave({ ...touch, ...c, offerId: v })} label="Connected free offer" />
            <div style={{ fontSize: 11.5, color: B.dim, marginTop: 6, lineHeight: 1.5 }}>{role.cta}</div>
          </div>
        </>
      )}
    />
  );
}


/* The channel, as a pill that is also the picker. A native select underneath
   so it works by keyboard and on a phone; the pill is what shows. Amber until
   chosen — a touch with no channel is something still to set. */
function ChannelPicker({ value, onChange }) {
  const def = channelDef(value);
  const unset = !def.id;
  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <span style={{
        padding: "2px 9px", borderRadius: 20, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
        color: unset ? B.amber : B.teal, background: unset ? B.amberQuiet : B.tealQuiet,
        border: `1px solid ${unset ? B.amberLine : B.tealLine}`,
      }}>{def.label} ▾</span>
      <select value={def.id} onChange={(e) => onChange(e.target.value)} aria-label="Channel"
        style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%" }}>
        <option value="" disabled>Choose channel</option>
        {CHANNELS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
    </span>
  );
}


/* ── Writing a touch with Brill ───────────────────────────────────────────────
   ONE call to Brill for one touch. COST: one API call per message; her locked
   work rides along, scoped by CONTEXT_SCOPES.outreach_copy to who she is
   writing to and how she sounds — four sections, the smallest of the drafting
   scopes, because a touch is a short message. */
export async function touchDraftReply({ token, sequence, touch, touches = [], offers = [], chatContext = {}, messages, whole = false }) {
  const ctx = buildMomentumContext({
    moduleId: "outreach_copy",
    acceleratorOutputs: chatContext.acceleratorOutputs,
    activeNiche: chatContext.activeNiche,
    isStale: (id, name) => {
      const stamp = chatContext.outputStamps?.[id]?.[name];
      return !!(chatContext.activeNiche && stamp && stamp !== chatContext.activeNiche.id);
    },
  });
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({
      moduleId: "outreach_copy",
      // A touch is short; a whole sequence of up to seven or so is not.
      max_tokens: whole ? 3000 : 1200,
      moduleLabel: "Outreach touch",
      archetype: chatContext.userSettings?.archetype || null,
      archetypePair: chatContext.userSettings?.archetypePair || null,
      messages: [
        ...(ctx ? [{ role: "user", content: ctx }] : []),
        /* Rebuilt per message from the touch as it stands. */
        { role: "user", content: whole ? buildSequenceContext({ sequence, touches, offers }) : buildTouchContext({ sequence, touch, touches, offers }) },
        ...messages,
      ],
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || "Something went wrong reaching Brill — try again in a moment. Nothing in this touch was touched; you can always type it yourself.");
  return data.content?.find((b) => b.type === "text")?.text || "";
}


/* The shared Drafter (drafter.jsx) on one touch — the same screen a website
   section and a course block are written on. The pager moves touch to touch,
   which is the one-at-a-time view her spec asked for. */
/* Named once: the bar renders it (`draftingBack` on OutreachShell) and Next's
   last step reads its words off this same string. */
export const TOUCH_BACK = "← Back to the sequence";


export function OutreachDrafter({ sequence, touch, touches = [], offers = [], chatContext = {}, onSave, onBack, onGoToTouch, startScope = "block", nextRecord = null }) {
  /* THE CHAT IS KEPT, per sequence (supabase/0049). */
  const keep = useRecordKeep({ tool: "outreach", recordId: sequence?.id, blockKey: touch?.id, liveKeys: touches.map((t) => t.id) });
  if (!sequence || !touch) return null;
  const kind = touchKindOf(touch);
  const channel = channelDef(touch.channel);
  const has = (mod, name) => {
    const outputs = chatContext.acceleratorOutputs?.[mod] || {};
    return name ? !!outputs[name] : Object.keys(outputs).length > 0;
  };
  const contextItems = [
    { label: "Your voice", on: has("voice", "Voice Profile") },
    { label: "Your client", on: has("client", "Ideal Client Profile") },
    { label: "What it's for", on: !!sequenceTypeDef(sequence.type) },
    { label: "The other touches", on: touches.some((t) => t.id !== touch.id && touchWords(t) > 0) },
  ];
  const index = touches.findIndex((t) => t.id === touch.id);

  return (
    <Drafter
      /* Recording the voice (supabase/0030): where a Lock lands. */
      voice={{ tool: "outreach_copy", recordType: "outreach_touch", blockId: touch?.id }}
      keep={keep}
      libraryKinds={["story", "testimonial"]}
      fieldProps={{ usedAs: { type: "sequence", id: sequence?.id, label: sequenceLabel(sequence) } }}
      kind={kind}
      content={{ subject: touch.subject || "", body: touch.body || "" }}
      onCommit={(content) => onSave({ ...touch, ...content })}
      ask={({ token, messages, content }) => touchDraftReply({
        token, sequence, touch: { ...touch, ...content }, touches, offers, chatContext, messages,
      })}
      parse={(reply, content) => {
        const block = parseTouchBlock(reply, touch);
        return block ? applyTouchBlock(content, block, touch) : null;
      }}
      strip={stripTouchBlock}
      title={`Write ${roleDef(touch.touchRole).label.toLowerCase()}`}
      opener={`I'm on ${touchLabel(touch).toLowerCase()}${touch.of ? ` of ${touch.of}` : ""} in ${sequenceLabel(sequence)}${channel.id ? `, going out on ${channel.label}` : ""}. Tell me who it's for and anything real about them — or say "draft it" and I'll leave a gap where the specific detail goes.`}
      suggestions={{
        first: ["Draft it", "I'll tell you who it's for"],
        later: ["Shorter", "Make it sound more like me", "Warmer"],
      }}
      contextItems={contextItems}
      pager={onGoToTouch && index >= 0 ? {
        items: touches.map((t) => ({ id: t.id, label: touchLabel(t), written: touchWords(t) > 0 })),
        index,
        onGo: onGoToTouch,
        noun: "Touch",
      } : null}
      chromeInBar
      backLabel={TOUCH_BACK} onBack={onBack}
      startScope={startScope} nextRecord={nextRecord}
      /* THE WHOLE SEQUENCE (decision 6: Outreach's default door). Every touch's
         fields on the right; one reply can fill several; one Lock fills them.
         COST: each message sends every touch, so it is dearer than a touch. */
      record={{
        blockLabel: "Touch",
        label: "Sequence",
        noun: "sequence",
        title: `Write ${sequenceLabel(sequence)}`,
        heading: sequenceLabel(sequence),
        opener: `We're on the whole of ${sequenceLabel(sequence)} — ${touches.length} touches. Tell me who it's for and anything real about them, or say "draft it all" and I'll write every touch, leaving gaps where a specific detail goes.`,
        suggestions: { first: ["Draft it all", "I'll tell you who it's for"], later: ["Make them fit together", "Shorter", "Make it sound more like me"] },
        blocks: touches.map((t) => ({ id: t.id, kind: touchKindOf(t), content: { subject: t.subject || "", body: t.body || "" } })),
        ask: ({ token, messages, contents }) => touchDraftReply({
          token, sequence, whole: true, offers, chatContext, messages,
          touches: touches.map((t) => ({ ...t, ...(contents[t.id] || {}) })),
        }),
        parse: (reply, contents) => {
          const byTouch = parseSequenceBlock(reply, touches);
          if (!byTouch) return null;
          const out = {};
          for (const t of touches) {
            if (byTouch[t.id]) out[t.id] = applyTouchBlock(contents[t.id] || {}, byTouch[t.id], t);
          }
          return out;
        },
        strip: stripTouchBlock,
        onCommitBlock: (id, content) => {
          const t = touches.find((x) => x.id === id);
          return t ? onSave({ ...t, ...content }) : null;
        },
      }}
      where={sequenceLabel(sequence)}
    />
  );
}
